import axios from 'axios';
import { API_URL, STORAGE_KEYS } from '../utils/constants';

const ACADEMY_API_URL = process.env.REACT_APP_ACADEMY_API_URL || API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const academyApi = axios.create({
  baseURL: ACADEMY_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
const attachAuthInterceptor = (client) => {
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

attachAuthInterceptor(api);
attachAuthInterceptor(academyApi);

// Handle response errors
const attachUnauthorizedHandler = (client) => {
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

attachUnauthorizedHandler(api);
attachUnauthorizedHandler(academyApi);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  googleAuth: (credential) => api.post('/auth/google', { credential }),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  getCurrentUser: () => api.get('/auth/me'),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  completeProfile: (data) => api.post('/auth/complete-profile', data),
  exportUsers: () => api.get('/auth/export-users'),
  deleteAccount: () => api.delete('/auth/account'),
  suspendAccount: () => api.post('/auth/account/suspend'),
  reactivateAccount: (credentials) => api.post('/auth/reactivate', credentials)
};

// Posts API
export const postsAPI = {
  getAll: (params) => api.get('/posts', { params }),
  getFollowing: (params) => api.get('/posts/following', { params }),
  getById: (id) => api.get(`/posts/${id}`),
  create: (postData) => api.post('/posts', postData),
  update: (id, postData) => api.put(`/posts/${id}`, postData),
  deletePost: (id) => api.delete(`/posts/${id}`),
  getByCategory: (category, params) => api.get(`/posts/category/${category}`, { params }),
  search: (query) => api.get('/posts/search', { params: { q: query } }),
  like: (id) => api.post(`/posts/${id}/like`),
  unlike: (id) => api.delete(`/posts/${id}/like`),
  report: (id, payload) => api.post(`/posts/${id}/report`, payload)
};

// Comments API
export const commentsAPI = {
  getByPost: (postId) => api.get(`/posts/${postId}/comments`),
  getByPostId: (postId) => api.get(`/posts/${postId}/comments`),
  create: (commentData) => api.post(`/posts/${commentData.postId}/comments`, { 
    content: commentData.content || commentData.text,
    parentId: commentData.parentId 
  }),
  update: (commentId, commentData) => api.put(`/comments/${commentId}`, commentData),
  delete: (commentId) => api.delete(`/comments/${commentId}`),
  react: (commentId, reactionType) => api.post(`/comments/${commentId}/reactions`, { reactionType })
};

// Reactions API
export const reactionsAPI = {
  getByPost: (postId) => api.get(`/posts/${postId}/reactions`),
  getByPostId: (postId) => api.get(`/posts/${postId}/reactions`),
  toggle: (postId, reactionType) => api.post(`/posts/${postId}/reactions`, { reactionType }),
  add: (postId, reactionType) => api.post(`/posts/${postId}/reactions`, { reactionType }),
  remove: (postId) => api.delete(`/posts/${postId}/reactions`),
  getUserReaction: (postId) => api.get(`/posts/${postId}/reactions/user`)
};

// Users API
export const usersAPI = {
  getProfile: (userId) => api.get(`/users/${userId}`),
  getProfileByUsername: (username) => api.get(`/users/profile/${username}`),
  search: (query) => api.get('/users/search', { params: { q: query } }),
  getFollowStatus: (identifier) => api.get(`/users/follow/${identifier}/status`),
  follow: (identifier) => api.post(`/users/follow/${identifier}`),
  unfollow: (identifier) => api.delete(`/users/follow/${identifier}`),
  getFollowers: (identifier) => api.get(`/users/${identifier}/followers`),
  getFollowing: (identifier) => api.get(`/users/${identifier}/following`),
  getLikes: (identifier) => api.get(`/users/${identifier}/likes`),
  updateProfile: (userData) => api.put('/users/profile/update', userData),
  uploadAvatar: (formData) => api.post('/users/avatar/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  changePassword: (passwordData) => api.put('/users/password/change', passwordData),
  sendPhoneVerification: (phone) => api.post('/users/phone/send-verification', { phone }),
  verifyPhone: (phone, code) => api.post('/users/phone/verify', { phone, code }),
  getSettings: () => api.get('/users/settings'),
  updateSettings: (settings) => api.put('/users/settings', settings),
  createSupportTicket: (payload) => api.post('/users/support-tickets', payload),
  getVerificationRequests: () => api.get('/users/verification-requests'),
  requestVerification: (payload) => api.post('/users/verification-requests', payload),
  exportData: () => api.get('/users/export-data', { responseType: 'blob' })
};

// Favorites API
export const favoritesAPI = {
  getAll: () => api.get('/favorites'),
  check: (postId) => api.get(`/favorites/check/${postId}`),
  add: (postId) => api.post(`/favorites/${postId}`),
  remove: (postId) => api.delete(`/favorites/${postId}`),
  getCount: () => api.get('/favorites/count')
};

// Academy API
export const academyAPI = {
  getDashboard: () => academyApi.get('/academy/dashboard'),
  getCourse: (identifier) => academyApi.get(`/academy/course/${identifier}`),
  enroll: (identifier) => academyApi.post(`/academy/course/${identifier}/enroll`),
  getLesson: (courseIdentifier, lessonSlug) => academyApi.get(`/academy/lesson/${courseIdentifier}/${lessonSlug}`),
  startLesson: (courseIdentifier, lessonSlug) => academyApi.post(`/academy/lesson/${courseIdentifier}/${lessonSlug}/start`),
  answerStep: (courseIdentifier, lessonSlug, payload) => academyApi.post(`/academy/lesson/${courseIdentifier}/${lessonSlug}/answer`, payload),
  getMyStats: () => academyApi.get('/academy/me/stats'),
  getDailyChallenge: () => academyApi.get('/academy/daily-challenge')
};

// Admin API
export const adminAPI = {
  // Dashboard Stats
  getStats: () => api.get('/admin/stats'),
  getChartData: () => api.get('/admin/charts'),
  getRecentActivity: (limit = 10) => api.get('/admin/activity', { params: { limit } }),
  
  // Support Tickets
  getSupportTickets: (filter) => api.get('/admin/support-tickets', { params: { filter } }),
  getTicket: (id) => api.get(`/admin/support-tickets/${id}`),
  updateTicketStatus: (id, status) => api.put(`/admin/support-tickets/${id}/status`, { status }),
  assignTicket: (id, adminId) => api.put(`/admin/support-tickets/${id}/assign`, { adminId }),
  resolveTicket: (id, response) => api.put(`/admin/support-tickets/${id}/resolve`, { response }),
  
  // Verification Requests
  getVerificationRequests: (params) => api.get('/admin/verification-requests', { params }),
  updateVerificationRequest: (id, payload) => api.put(`/admin/verification-requests/${id}`, payload),
  
  // Content Reports
  getReports: (filter) => api.get('/admin/reports', { params: { filter } }),
  getReport: (id) => api.get(`/admin/reports/${id}`),
  handleReport: (id, action, reason) => api.put(`/admin/reports/${id}/handle`, { action, reason }),
  
  // User Management
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUserPermissions: (id, permissions) => api.put(`/admin/users/${id}/permissions`, permissions),
  updateUserRole: (id, hierarchical_role) => api.put(`/admin/users/${id}/role`, { hierarchical_role }),
  updateUserVerification: (id, verification_level) => api.put(`/admin/users/${id}/verification`, { verification_level }),
  banUser: (id, banned, reason) => api.put(`/admin/users/${id}/ban`, { banned, reason }),
  
  // Content Management
  getContentPosts: (params) => api.get('/admin/content/posts', { params }),
  hidePost: (id, hidden, reason) => api.put(`/admin/content/posts/${id}/hide`, { hidden, reason }),
  deletePost: (id) => api.delete(`/admin/content/posts/${id}`),
  
  // System Health
  getSystemHealth: () => api.get('/admin/system/health'),
  getSystemLogs: (params) => api.get('/admin/system/logs', { params }),
  
  // Data Export
  exportData: (type) => api.get(`/admin/export/${type}`, { responseType: 'blob' })
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`)
};

export default api;
